import React, { useState } from 'react';
import { Drawer, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import inventoryService, { type HeatLedgerRow, type HeatStockRow } from '@/services/inventory.service';
import { MtcDocumentLink } from '@/components/documents/MtcDocumentLink';
import { CriticalItemTag } from '@/components/inventory/criticalItemUi';
import { formatMoneyWithProfile, formatQuantity, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';

const { Text } = Typography;

const qty = (v: number | string | null | undefined) => formatQuantity(v ?? 0, { maximumFractionDigits: 4 });
const money = (v: number) => formatMoneyWithProfile(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);

export const heatStockRowKey = (r: HeatStockRow) =>
  [r.item_id, r.warehouse_id, r.location_id, r.heat_number, r.dimension, r.ownership_type, r.owner_name ?? ''].join('|');

function SourceCell({ row }: { row: HeatStockRow }) {
  const s = row.source;
  if (!s) return <Text type="secondary">—</Text>;
  const date = s.source_date ? dayjs(s.source_date).format('DD MMM YYYY') : '';
  if (s.source_type === 'GRN') {
    return (
      <Space direction="vertical" size={0}>
        <Link to={`/scm/procurement/grn/${s.source_id}`}>{s.source_number || 'GRN'}</Link>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {[date, s.supplier_name, s.po_number].filter(Boolean).join(' · ')}
        </Text>
      </Space>
    );
  }
  if (s.source_type === 'StockAdjustment') {
    return (
      <Space direction="vertical" size={0}>
        <Link to={`/scm/warehouse/stock-adjustment/${s.source_id}`}>{s.source_number || 'Stock adjustment'}</Link>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Stock adjustment{date ? ` · ${date}` : ''}
        </Text>
      </Space>
    );
  }
  return (
    <Space direction="vertical" size={0}>
      <Link to="/scm/inventory/opening-stock">{s.source_number || 'Opening stock'}</Link>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Opening stock{date ? ` · ${date}` : ''}
      </Text>
    </Space>
  );
}

function HeatMovementsDrawer({ row, onClose }: { row: HeatStockRow | null; onClose: () => void }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ['heat-ledger', row?.item_id, row?.heat_number, row?.dimension],
    queryFn: () =>
      inventoryService.getHeatLedger({
        item_id: row!.item_id,
        heat_number: row!.heat_number,
        dimension: row!.dimension || undefined,
      }),
    enabled: Boolean(row?.heat_number),
  });

  const columns: ColumnsType<HeatLedgerRow> = [
    {
      title: 'Date',
      dataIndex: 'transaction_time',
      width: 110,
      render: (v: string, r) => dayjs(v || r.transaction_date).format('DD MMM YYYY'),
    },
    { title: 'Type', dataIndex: 'transaction_type', width: 130 },
    {
      title: 'Document',
      key: 'doc',
      render: (_: unknown, r) =>
        r.voucher_type === 'GRN' && r.voucher_id ? (
          <Link to={`/scm/procurement/grn/${r.voucher_id}`}>{r.voucher_number}</Link>
        ) : (
          [r.voucher_type, r.voucher_number].filter(Boolean).join(' ') || '—'
        ),
    },
    {
      title: 'Warehouse',
      key: 'wh',
      width: 120,
      render: (_: unknown, r) => r.warehouse?.warehouse_code || r.warehouse?.warehouse_name || '—',
    },
    {
      title: 'Project',
      key: 'project',
      width: 140,
      render: (_: unknown, r) => r.project_number || r.project_name || '—',
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      align: 'right',
      width: 90,
      render: (v: number | string) => (
        <Text type={Number(v) < 0 ? 'danger' : 'success'}>
          {Number(v) > 0 ? '+' : ''}
          {qty(v)}
        </Text>
      ),
    },
    { title: 'Balance', dataIndex: 'balance_qty', align: 'right', width: 90, render: (v: number | string) => qty(v) },
  ];

  return (
    <Drawer
      open={Boolean(row)}
      onClose={onClose}
      width={880}
      title={row ? `Heat ${row.heat_number}${row.dimension ? ` / ${row.dimension}` : ''} — ${row.item_code}` : ''}
      extra={
        row?.heat_number ? (
          <Link to={`/mfg/traceability-matrix?heat_number=${encodeURIComponent(row.heat_number)}`}>
            Where used (traceability matrix)
          </Link>
        ) : null
      }
    >
      <Table<HeatLedgerRow>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        columns={columns}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
      />
    </Drawer>
  );
}

export interface HeatStockTableProps {
  rows: HeatStockRow[];
  loading?: boolean;
  /** Hide item columns when the table is already scoped to one item (item master). */
  hideItem?: boolean;
}

/** Heat / dimension stock buckets with MTC reference, receipt source and traceability flags. */
export const HeatStockTable: React.FC<HeatStockTableProps> = ({ rows, loading, hideItem }) => {
  const [ledgerRow, setLedgerRow] = useState<HeatStockRow | null>(null);

  const columns: ColumnsType<HeatStockRow> = [
    ...(hideItem
      ? []
      : ([
          {
            title: 'Item',
            key: 'item',
            width: 220,
            fixed: 'left',
            render: (_: unknown, r: HeatStockRow) => (
              <Space direction="vertical" size={0}>
                <Space size={4}>
                  <Link to={`/scm/inventory/items/${r.item_id}`}>{r.item_code}</Link>
                  <CriticalItemTag critical={r.is_critical} />
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {r.item_name}
                </Text>
              </Space>
            ),
          },
        ] as ColumnsType<HeatStockRow>)),
    {
      title: 'Warehouse / Location',
      key: 'wh',
      width: 150,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <span>{r.warehouse_code || r.warehouse_name || '—'}</span>
            {r.warehouse_type === 'Quarantine' && (
              <Tooltip title="Quarantine warehouse — not counted in Stock on hand / available to issue">
                <Tag color="volcano">Quarantine</Tag>
              </Tooltip>
            )}
          </Space>
          {r.location_code || r.location_name ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.location_code || r.location_name}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Heat No',
      dataIndex: 'heat_number',
      width: 140,
      render: (v: string, r) =>
        v ? (
          <Tooltip title="Show movements for this heat">
            <a onClick={() => setLedgerRow(r)}>{v}</a>
          </Tooltip>
        ) : (
          <Text type="secondary">(no heat)</Text>
        ),
    },
    { title: 'Dimension', dataIndex: 'dimension', width: 120, render: (v: string) => v || '—' },
    { title: 'On hand', dataIndex: 'on_hand_quantity', align: 'right', width: 95, render: qty },
    {
      title: 'Reserved',
      dataIndex: 'reserved_quantity',
      align: 'right',
      width: 90,
      render: (v: number) => (v > 0 ? <Text type="warning">{qty(v)}</Text> : qty(v)),
    },
    { title: 'Free', dataIndex: 'free_quantity', align: 'right', width: 90, render: qty },
    { title: 'UOM', dataIndex: 'uom', width: 70 },
    { title: 'Value', dataIndex: 'total_value', align: 'right', width: 110, render: money },
    {
      title: 'MTC',
      key: 'mtc',
      width: 220,
      render: (_: unknown, r) =>
        r.mtc_document_id ? (
          <Space direction="vertical" size={0}>
            <MtcDocumentLink documentId={r.mtc_document_id} documentNumber={r.mtc_document_number} compact />
            {r.mtc_reference_number ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cert no: {r.mtc_reference_number}
              </Text>
            ) : null}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    { title: 'Received from', key: 'source', width: 220, render: (_: unknown, r) => <SourceCell row={r} /> },
    {
      title: 'Ownership',
      key: 'own',
      width: 130,
      render: (_: unknown, r) =>
        r.ownership_type === 'COMPANY' ? (
          'Company'
        ) : (
          <Tooltip title={r.owner_name || undefined}>
            <Tag color={r.ownership_type === 'CUSTOMER' ? 'purple' : 'geekblue'}>
              {r.ownership_type === 'CUSTOMER' ? 'Customer-owned' : 'Consignment'}
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Flags',
      key: 'flags',
      width: 120,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {r.heat_missing && (
            <Tooltip title="Critical / heat-mandatory item held without a heat number — cannot be traced">
              <Tag color="red">No heat</Tag>
            </Tooltip>
          )}
          {r.mtc_missing && (
            <Tooltip title="No MTC linked on the posted GRN or opening stock for this heat">
              <Tag color="orange">No MTC</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table<HeatStockRow>
        size="small"
        rowKey={heatStockRowKey}
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: hideItem ? 1500 : 1720 }}
        pagination={{ pageSize: 50, showSizeChanger: true, hideOnSinglePage: true }}
      />
      <HeatMovementsDrawer row={ledgerRow} onClose={() => setLedgerRow(null)} />
    </>
  );
};

export default HeatStockTable;
